import type { BalanceMatrixRow } from '@lms/shared-types'
import { csvField } from '../../lib/leaves-csv'
import { LEAVE_TYPE_SHORT } from '../../lib/leave-type-meta'
import { columnLeaveTypesFor, matrixCellDisplay, type RosterMetric } from './matrix-cell'

/**
 * CSV for the wide balance / entitlement roster — one row per employee, one
 * column per leave type, in the SAME column order and with the SAME cell values
 * the page renders (both go through {@link matrixCellDisplay}). So the download
 * is a faithful snapshot of the table, non-applicable cells included ("—").
 */

/** Column header for a leave-type column. Mirrors the on-screen header: the
 *  short code — upper-cased to match the table's `text-transform: uppercase` —
 *  plus the "(taken)" qualifier on unpaid in the remaining view (unpaid has no
 *  entitlement, so the column shows days taken, not a balance). */
function columnHeader(lt: string, metric: RosterMetric): string {
  const short = (LEAVE_TYPE_SHORT[lt as keyof typeof LEAVE_TYPE_SHORT] ?? lt).toUpperCase()
  return lt === 'unpaid' && metric === 'remaining' ? `${short} (taken)` : short
}

/** Build the CSV string for a set of roster rows. */
export function balancesMatrixToCsv(
  rows: ReadonlyArray<BalanceMatrixRow>,
  metric: RosterMetric,
): string {
  const columns = columnLeaveTypesFor(metric)
  const header = ['Employee', ...columns.map((lt) => columnHeader(lt, metric))]
  const lines = [header.map(csvField).join(',')]
  for (const row of rows) {
    const cells = [
      csvField(row.employeeName),
      ...columns.map((lt) => csvField(matrixCellDisplay(row, lt, metric))),
    ]
    lines.push(cells.join(','))
  }
  return `${lines.join('\n')}\n`
}
